import { useTranslation } from "react-i18next";
import styles from "../css/CollectionAddScreen.module.css";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import PlaceHolderTextField from "../../common/PlaceHolderTextField";
import BigButton from "../../common/BigButton";
import SpacingView from "../../common/view/SpaingView";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { useEffect, useState } from "react";
import LoadingScreen from "../../common/LoadingScreen";
import { handleErrorAlert } from "../../common/OnevocaServerError";
import { putColletion } from "../service/collectionAPI";
import CollectionColorSettingView from "../view/CollectionColorSettingView";
import { IconColor } from "../../common/model/IconColor";
import { IconColorSet } from "../../common/model/IconColorSet";
import ColorSelectScreen from "../../common/screen/ColorSelectScreen";
import { IconColorList } from "../../common/model/IconColorList";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface CollectionAddScreenProps {
  open: boolean;
  onCreate: () => void;
  onClose: () => void;
}

export default function CollectionAddScreen(props: CollectionAddScreenProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();

  const [showLoading, setShowLoading] = useState(false);
  const [openColorSelectScreen, setOpenColorSelectScreen] = useState(false);

  const [selectedColor, setSelectedColor] = useState<IconColor>(
    IconColor.from(IconColorSet.gray)
  );
  const [collectionName, setCollectionName] = useState("");
  const [doneButtonStyle, setDoneButtonStyle] = useState<"normal" | "disable">(
    "disable"
  );

  useEffect(() => {
    setCollectionName("");
    setDoneButtonStyle("disable");
  }, [props.open]);

  if (!props.open) {
    return null;
  }

  function updateCollectionName(text: string) {
    setCollectionName(text);
  }

  function updateCollectionColor(color: IconColor) {
    setSelectedColor(color);
  }

  function updateDoneButtonStatus(text: string) {
    const length = text.length;
    if (length === 0 || length > 40) {
      setDoneButtonStyle("disable");
    } else {
      setDoneButtonStyle("normal");
    }
  }

  async function createCollection() {
    setShowLoading(true);
    try {
      await putColletion(collectionName, selectedColor.name);
      props.onCreate();
      props.onClose();
    } catch (error) {
      handleErrorAlert(error);
    } finally {
      setShowLoading(false);
    }
  }

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  return (
    <>
      <div className={styles.stack} onClick={props.onClose}>
        <div className={styles.main}>
          <div className={styles.container} onClick={handleOnClickContainer}>
            <div className={styles.topContainer}>
              <ContainerNavigationView
                open={true}
                title={t("CollectionAddNavTitle")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
              />
              <SpacingView height={24} />
              <PlaceHolderTextField
                hint={t("CollectionAddPlaceholder")}
                minCount={1}
                maxCount={40}
                lineStyle="single"
                autoFocus={true}
                handleTextChange={(text: string) => {
                  updateCollectionName(text);
                  updateDoneButtonStatus(text);
                }}
              />
              <SpacingView height={24} />
              <CollectionColorSettingView
                open={true}
                item={selectedColor}
                onClick={() => {
                  setOpenColorSelectScreen(true);
                }}
              />
            </div>
            <div className={styles.bottomContainer}>
              <BigButton
                title={t("Done")}
                variant={doneButtonStyle}
                handleClick={() => {
                  if (doneButtonStyle === "normal") {
                    createCollection();
                  }
                }}
              />
            </div>
          </div>
        </div>
        <ColorSelectScreen
          open={openColorSelectScreen}
          onSelectItem={updateCollectionColor}
          list={IconColorList.collections}
          onClose={() => {
            setOpenColorSelectScreen(false);
          }}
        />
        {showLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
